import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Camera, GraduationCap, UsersThree, BookOpen, WhatsappLogo, CalendarStar, X } from "@phosphor-icons/react";

const LS_INVITED = "ap_invited_classmate";
const LS_DISMISS = "ap_onboarding_dismissed";

export default function OnboardingChecklist({ stats }) {
  const [profile, setProfile] = useState(null);
  const [invited, setInvited] = useState(() => { try { return localStorage.getItem(LS_INVITED) === "1"; } catch { return false; } });
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(LS_DISMISS) === "1"; } catch { return false; } });

  useEffect(() => { api.get("/profiles/me").then((r) => setProfile(r.data)).catch(() => setProfile({})); }, []);

  const items = useMemo(() => {
    const p = profile || {};
    const edu = p.education || p.institutions || [];
    return [
      { key: "photo", icon: Camera, label: "Add a profile photo", body: "Help classmates recognise you.", to: "/profile/edit", done: !!(p.avatar_file_id || p.photo_file_id) },
      { key: "education", icon: GraduationCap, label: "Add your education", body: "Your school, college or batch.", to: "/profile/edit", done: (edu?.length || 0) > 0 },
      { key: "classmates", icon: UsersThree, label: "Find your classmates", body: "Connect with people from your years.", to: "/find", done: (stats?.connections || 0) > 0 },
      { key: "memory", icon: BookOpen, label: "Share a memory", body: "A teacher, a trip, a moment.", to: "/memory-lane", done: (p.memories_count || 0) > 0 },
      { key: "reunion", icon: CalendarStar, label: "Join a reunion group", body: "Reconnect around an event.", to: "/groups", done: (stats?.groups || 0) > 0 || (stats?.events || 0) > 0 },
      { key: "invite", icon: WhatsappLogo, label: "Invite a classmate", body: "Share on WhatsApp — better together.", action: "invite", done: invited },
    ];
  }, [profile, stats, invited]);

  const doneCount = items.filter((i) => i.done).length;
  const pct = Math.round((doneCount / items.length) * 100);

  const invite = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://alumnipulse.net";
    const text = encodeURIComponent(`I just joined our alumni community on AlumniPulse. Let's find our classmates and preserve our memories together: ${origin}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
    try { localStorage.setItem(LS_INVITED, "1"); } catch { /* ignore */ }
    setInvited(true);
  };
  const dismiss = () => { try { localStorage.setItem(LS_DISMISS, "1"); } catch { /* ignore */ } setDismissed(true); };

  if (dismissed || (profile && pct === 100)) return null;

  return (
    <Card className="relative border-secondary/25 bg-accent/30 p-6 shadow-sm" data-testid="onboarding-checklist">
      <button onClick={dismiss} className="absolute right-4 top-4 text-muted-foreground transition-colors hover:text-foreground" aria-label="Dismiss checklist" data-testid="onboarding-dismiss"><X size={16} /></button>
      <p className="overline text-secondary mb-1">Getting started</p>
      <h2 className="font-heading text-xl font-medium">Let's set up your alumni home</h2>
      <p className="mt-1 text-sm text-muted-foreground">A few quick steps unlock the best of your community.</p>
      <div className="mt-4 flex items-center gap-3">
        <Progress value={pct} className="h-2 flex-1" data-testid="onboarding-progress" />
        <span className="text-sm font-medium text-secondary tabular-nums">{doneCount}/{items.length}</span>
      </div>
      <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
        {items.map((it) => {
          const inner = (
            <div className={`flex h-full items-start gap-3 rounded-xl border p-3.5 transition-colors ${it.done ? "border-secondary/30 bg-secondary/[0.06]" : "border-border/60 bg-background hover:border-secondary/40"}`}>
              {it.done ? <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-secondary" /> : <it.icon size={22} className="mt-0.5 shrink-0 text-primary" />}
              <div className="min-w-0">
                <div className={`text-sm font-medium ${it.done ? "text-muted-foreground line-through" : ""}`}>{it.label}</div>
                <div className="text-xs text-muted-foreground">{it.body}</div>
              </div>
            </div>
          );
          return it.action === "invite"
            ? <button key={it.key} onClick={invite} className="text-left" data-testid="onboarding-item-invite">{inner}</button>
            : <Link key={it.key} to={it.to} data-testid={`onboarding-item-${it.key}`}>{inner}</Link>;
        })}
      </div>
    </Card>
  );
}
